import React, {
  createContext,
  PropsWithChildren,
  useState,
  useContext,
  useCallback,
  useRef,
} from 'react';
import {
  getVoyageWithShipmentsAggregated,
  getTbnVoyages,
} from '../../services/VoyageRepository';
import { useReportingFilters } from './ReportingFiltersContext';
import type { ApiRecord, RequestParams } from './types';

export type VoyageId = string | number;

export interface ReportingVoyageDataContextValue {
  voyages: ApiRecord[];
  voyagesBase: ApiRecord[];
  currentPage: number;
  totalPages: number;
  fetchVoyagesPage: (page: number, searchOverride?: string) => Promise<void>;
  selectedVoyageId: VoyageId | null;
  setSelectedVoyageId: (id: VoyageId) => void;
  clearSelectedVoyageId: () => void;
  resetSelectedVoyageId: () => void;
  tbnVoyages: ApiRecord[];
  fetchTbnVoyages: () => Promise<void>;
}

const ReportingVoyageDataContext =
  createContext<ReportingVoyageDataContextValue | null>(null);

export const ReportingVoyageDataProvider = ({
  children,
}: PropsWithChildren) => {
  const { filters, buildParams, activeScenario, showError } =
    useReportingFilters();

  const [voyages, setVoyages] = useState<ApiRecord[]>([]);
  const [voyagesBase, setVoyagesBase] = useState<ApiRecord[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [tbnVoyages, setTbnVoyages] = useState<ApiRecord[]>([]);
  const [selectedVoyageId, setSelectedVoyageIdState] =
    useState<VoyageId | null>(null);
  const selectedVoyageIdRef = useRef<VoyageId | null>(null);

  // ── REFACTORED: Stock data now comes from the backend in the same response ──
  // No more per-shipment getAggregatedStock calls.
  const fetchVoyagesPage = useCallback(
    async (page: number, searchOverride?: string) => {
      try {
        setCurrentPage(page);
        const paramsObj = Object.fromEntries(buildParams().entries());
        const search = searchOverride ?? filters.search?.trim();
        const params: RequestParams = {
          ...paramsObj,
          page: page - 1,
          size: 10,
        };
        if (search) {
          params.search = search;
        }
        if (selectedVoyageIdRef.current) {
          params.id = selectedVoyageIdRef.current;
        }

        const scenarioFetch = getVoyageWithShipmentsAggregated(params);

        // When scenario is active, also fetch base (without scenarioId) for comparison
        let baseFetch: Promise<ApiRecord> | undefined;
        if (activeScenario) {
          const baseParams = { ...params };
          delete baseParams.scenarioId;
          baseFetch = getVoyageWithShipmentsAggregated(baseParams).catch(
            () => ({ content: [], totalPages: 0 })
          );
        }

        const response = await scenarioFetch;
        const { content, totalPages } = response;

        // Stock is already attached to each shipment by the backend
        // (shipment.stock will be null when no matching stock data exists)
        setVoyages(content || []);
        setTotalPages(totalPages || 0);

        if (baseFetch) {
          const baseResponse = await baseFetch;
          setVoyagesBase(baseResponse.content || []);
        } else {
          setVoyagesBase([]);
        }
      } catch (error) {
        showError(error, 'fetchVoyagesPage');
        setVoyages([]);
        setVoyagesBase([]);
        setTotalPages(0);
      }
    },
    [buildParams, filters.search, activeScenario, showError]
  );

  const setSelectedVoyageId = useCallback(
    (id: VoyageId) => {
      selectedVoyageIdRef.current = id;
      setSelectedVoyageIdState(id);
      fetchVoyagesPage(1);
    },
    [fetchVoyagesPage]
  );

  const clearSelectedVoyageId = useCallback(() => {
    selectedVoyageIdRef.current = null;
    setSelectedVoyageIdState(null);
    fetchVoyagesPage(1);
  }, [fetchVoyagesPage]);

  // Resets the selected voyage without triggering a refetch — used by the
  // parent provider when filters change and all tabs are invalidated anyway.
  const resetSelectedVoyageId = useCallback(() => {
    selectedVoyageIdRef.current = null;
    setSelectedVoyageIdState(null);
  }, []);

  const fetchTbnVoyages = useCallback(async () => {
    try {
      const paramsObj = Object.fromEntries(buildParams().entries());
      // The banner only renders route/date/remark fields, so the dedicated endpoint
      // skips the shipment aggregation and stock enrichment entirely. Scenarios still
      // need the overlay engine, so they keep using the aggregated endpoint.
      if (activeScenario) {
        const response = await getVoyageWithShipmentsAggregated({
          ...paramsObj,
          ship: 'TBN',
          page: 0,
          size: 1000,
        });
        setTbnVoyages(response?.content || []);
        return;
      }
      setTbnVoyages((await getTbnVoyages(paramsObj)) || []);
    } catch (error) {
      setTbnVoyages([]);
    }
  }, [buildParams, activeScenario]);

  return (
    <ReportingVoyageDataContext.Provider
      value={{
        voyages,
        voyagesBase,
        currentPage,
        totalPages,
        fetchVoyagesPage,
        selectedVoyageId,
        setSelectedVoyageId,
        clearSelectedVoyageId,
        resetSelectedVoyageId,
        tbnVoyages,
        fetchTbnVoyages,
      }}
    >
      {children}
    </ReportingVoyageDataContext.Provider>
  );
};

export const useReportingVoyageData = (): ReportingVoyageDataContextValue => {
  const ctx = useContext(ReportingVoyageDataContext);
  if (ctx === null) {
    throw new Error(
      'useReportingVoyageData must be used within a <ReportingVoyageDataProvider>'
    );
  }
  return ctx;
};
